import Payment from './Payment';
import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Home } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStoreBySlug } from '../store/reducers/homeReducer';
import { normalizeStoreName } from '../utils/storeUtils';
import Headers from '../components/Headers';
import Footer from '../components/Footer';

const LegacyOrderReceived = () => {
    const { storeName: urlStoreName } = useParams();
    const location = useLocation();
    const { fromStore: stateFromStore } = location.state || {};
    const fromStore = urlStoreName || stateFromStore;
    const isStoreMode = !!urlStoreName;
    const dispatch = useDispatch();
    const { seller } = useSelector((state) => state.home);

    useEffect(() => {
        if (fromStore && !isStoreMode) {
            dispatch(fetchStoreBySlug(fromStore));
        }
    }, [fromStore, dispatch, isStoreMode]);

    // Theme logic - Only if NOT StoreMode
    const getThemeClass = () => {
        if (isStoreMode) return '';

        const themeSettings = seller?.themeSettings;
        const useCustomTheme = themeSettings?.useCustomTheme;

        // Custom theme always uses light-theme class (defaults to light colors)
        if (useCustomTheme) {
            return 'light-theme';
        }
        if (seller?.shopInfo?.theme === 'light') {
            return 'light-theme';
        }
        if (fromStore) {
            const normalizedStore = normalizeStoreName(fromStore);
            const cachedTheme = localStorage.getItem(`store-theme-${normalizedStore}`);
            if (cachedTheme === 'light') return 'light-theme';

            try {
                const cachedSettings = localStorage.getItem(`store-settings-${normalizedStore}`);
                if (cachedSettings) {
                    const settings = JSON.parse(cachedSettings);
                    if (settings.useCustomTheme) return 'light-theme';
                }
            } catch (e) { }
        }
        return '';
    };
    const themeClass = getThemeClass();

    // Apply theme class to body immediately on render if we have cached theme context
    // Only if NOT StoreMode
    if (!isStoreMode && themeClass === 'light-theme') {
        document.body.classList.add('light-theme');

        // If we have cached custom theme settings, apply them now to prevent flash
        if (fromStore) {
            try {
                const normalizedStore = normalizeStoreName(fromStore);
                const cachedSettings = localStorage.getItem(`store-settings-${normalizedStore}`);
                if (cachedSettings) {
                    const settings = JSON.parse(cachedSettings);
                    if (settings.useCustomTheme) {
                        const colors = settings.colors || {};
                        const setVar = (name, value) => {
                            if (value) document.body.style.setProperty(name, value);
                        };
                        const getContrastColor = (hexcolor) => {
                            if (!hexcolor || hexcolor === 'transparent') return null;
                            const hex = hexcolor.replace('#', '');
                            const r = parseInt(hex.slice(0, 2), 16);
                            const g = parseInt(hex.slice(2, 4), 16);
                            const b = parseInt(hex.slice(4, 6), 16);
                            const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                            return (yiq >= 128) ? '#000000' : '#ffffff';
                        };
                        const contrastColor = getContrastColor(colors.background);
                        setVar('--store-bg', colors.background);
                        setVar('--store-text', contrastColor);
                        setVar('--store-icon', contrastColor);
                        setVar('--store-button-bg', colors.buttonBg);
                        setVar('--store-button-text', colors.buttonText);
                        setVar('--store-product-card-bg', colors.productCardBg);
                        setVar('--store-header-bg', colors.headerBg);
                        setVar('--store-footer-bg', colors.footerBg);
                        setVar('--store-border', colors.border);
                        if (settings.typography?.fontFamily) {
                            const fontFamily = settings.typography.fontFamily;
                            const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;
                            if (!document.getElementById(fontId)) {
                                const link = document.createElement('link');
                                link.id = fontId;
                                link.rel = 'stylesheet';
                                link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                                document.head.appendChild(link);
                            }
                            setVar('--store-font-family', `"${fontFamily}", sans-serif`);
                        }
                    }
                }
            } catch (e) {
                console.error("Error applying cached theme:", e);
            }
        }
    }

    useEffect(() => {
        if (isStoreMode) return;

        if (seller?.shopInfo && fromStore) {
            const normalizedStore = normalizeStoreName(fromStore);
            localStorage.setItem(`store-theme-${normalizedStore}`, seller.shopInfo.theme);
            localStorage.setItem(`store-settings-${normalizedStore}`, JSON.stringify(seller.themeSettings || {}));

            const themeSettings = seller?.themeSettings;
            const useCustomTheme = themeSettings?.useCustomTheme;

            // Apply light-theme class when using custom theme or when theme is 'light'
            if (useCustomTheme || seller.shopInfo.theme === 'light') {
                document.body.classList.add('light-theme');
            } else {
                document.body.classList.remove('light-theme');
            }

            // Apply custom CSS variables when useCustomTheme is true
            if (useCustomTheme && themeSettings) {
                const colors = themeSettings.colors || {};
                const setVar = (name, value) => {
                    if (value) document.body.style.setProperty(name, value);
                };

                const getContrastColor = (hexcolor) => {
                    if (!hexcolor || hexcolor === 'transparent') return null;
                    const hex = hexcolor.replace('#', '');
                    const r = parseInt(hex.slice(0, 2), 16);
                    const g = parseInt(hex.slice(2, 4), 16);
                    const b = parseInt(hex.slice(4, 6), 16);
                    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                    return (yiq >= 128) ? '#000000' : '#ffffff';
                };

                const contrastColor = getContrastColor(colors.background);
                setVar('--store-bg', colors.background);
                setVar('--store-text', contrastColor);
                setVar('--store-icon', contrastColor);
                setVar('--store-button-bg', colors.buttonBg);
                setVar('--store-button-text', colors.buttonText);
                setVar('--store-product-card-bg', colors.productCardBg);
                setVar('--store-header-bg', colors.headerBg);
                setVar('--store-footer-bg', colors.footerBg);
                setVar('--store-input-bg', themeSettings.inputs?.background || colors.inputBg);
                setVar('--store-input-text', themeSettings.inputs?.text || colors.inputText);
                setVar('--store-input-border', themeSettings.inputs?.border || colors.inputBorder);
                setVar('--store-input-focus', themeSettings.inputs?.focusRing || '#000000');
                setVar('--store-input-radius', `${themeSettings.inputs?.radius || 20}px`);
                setVar('--store-select-bg', themeSettings.selects?.background || colors.inputBg);
                setVar('--store-select-text', themeSettings.selects?.text || colors.inputText);
                setVar('--store-select-border', themeSettings.selects?.border || colors.inputBorder);
                setVar('--store-select-focus', themeSettings.selects?.focusRing || '#000000');
                setVar('--store-select-radius', `${themeSettings.selects?.radius || 20}px`);
                setVar('--store-border', colors.border);
                setVar('--store-muted', colors.muted);
                setVar('--store-glass', colors.glass);

                // Load and apply Google Font
                if (themeSettings.typography?.fontFamily) {
                    const fontFamily = themeSettings.typography.fontFamily;
                    const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;

                    if (!document.getElementById(fontId)) {
                        const link = document.createElement('link');
                        link.id = fontId;
                        link.rel = 'stylesheet';
                        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                        document.head.appendChild(link);
                    }

                    setVar('--store-font-family', `"${fontFamily}", sans-serif`);
                    document.body.style.fontFamily = `"${fontFamily}", sans-serif`;
                }
            }
        }

        // Cleanup when unmounting or changing stores
        return () => {
            document.body.classList.remove('light-theme');
        };
    }, [seller, fromStore, isStoreMode]);

    return (
        <div className={!isStoreMode ? themeClass : ''}>
            {!isStoreMode && <Headers />}
            <div className="min-h-[60vh] bg-[var(--store-bg)] flex items-center justify-center py-20 px-4">
                <div className="max-w-lg w-full bg-[var(--store-card-bg)] rounded-2xl p-8 md:p-12 text-center shadow-2xl border border-[var(--store-border)] relative overflow-hidden">

                    {/* Background decoration */}
                    {/* <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 via-[var(--store-accent)] to-green-500"></div> */}

                    <div className="flex justify-center mb-8">
                        <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center relative">
                            <div className="absolute inset-0 rounded-full border border-green-500/20 animate-ping"></div>
                            <CheckCircle className="w-12 h-12 text-green-500" strokeWidth={2} />
                        </div>
                    </div>

                    <h1 className="text-3xl md:text-4xl font-bold text-[var(--store-text)] mb-4">Order Received</h1>

                    <p className="text-[var(--store-text-secondary)] text-lg mb-8 leading-relaxed">
                        Your order has been received. Open your payment page or order history to check payment confirmation and delivery progress.
                    </p>

                    <div className="bg-[var(--store-bg)] rounded-xl p-6 mb-2 border border-[var(--store-border)]">
                        <div className="flex items-center gap-4 text-left">
                            <div className="bg-[var(--store-card-bg)] p-3 rounded-lg">
                                <Package className="text-[var(--store-accent)]" size={24} />
                            </div>
                            <div>
                                <h3 className="text-[var(--store-text)] font-medium">What happens next?</h3>
                                <p className="text-sm text-[var(--store-text-muted)] mt-1">You will receive an email confirmation with your order details.</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex   justify-center">
                        {/* <Link 
                            to="/dashboard/my-orders" 
                            className="flex items-center justify-center gap-2 px-8 py-3 bg-[var(--store-accent)] text-black font-bold rounded-lg hover:bg-[var(--store-accent)]/90 transition-all"
                        >
                            View Order
                            <ArrowRight size={18} />
                        </Link> */}

                        <Link
                            to={fromStore ? `/${fromStore}` : "/"}
                            className="w-[100%] flex items-center justify-center gap-2 px-8 py-3 bg-[var(--store-button-bg)] text-[var(--store-button-text)] font-medium rounded-lg hover:opacity-90 transition-all border border-[var(--store-border)]"
                        >

                            {fromStore ? "Back to Store" : "Back to Home"}
                        </Link>
                    </div>
                </div>
            </div>
            {!isStoreMode && <Footer />}
        </div>
    );
};

function OrderReceived() {
  const location = useLocation();
  return new URLSearchParams(location.search).get('orderId') || location.state?.orderId ? <Payment /> : <LegacyOrderReceived />;
}
export default OrderReceived;
